import {
  Row, Col, Card, Table, Button, Input,
  Tag, DatePicker, Tooltip, Avatar, Image,
  Space, Select, Modal,
} from "antd";
import React, { useState, useContext, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import * as XLSX from "xlsx";
import { ShowToast, Severty } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import moment from "moment";
import { useNavigate } from "react-router";
import ShowTotal from "../../components/ShowTotal";
import apiPath from "../../constants/apiPath";
import UserFrom from "./UserFrom";
import ImportForm from "../../components/ImportForm";
import ConfirmationBox from "../../components/ConfirmationBox";
import { AuthContext } from "../../context/AuthContext";
import { CricketRole, UserStatus, USERTYPE } from "../../constants/conts";
import { UndoOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import FilterBar, { FilterBarFilters } from "../../components/FilterBar";
import { useLocation } from "react-router-dom";

const Search = Input.Search;
const { RangePicker } = DatePicker;

function UserManager() {
  const sectionName = "User";
  const { Option } = Select;
  const api = {
    status: apiPath.statusCustomer,
    delete: apiPath.deleteUser,
    addEdit: apiPath.addEditCustomer,
    // list: apiPath.listCustomer,
    importFile: apiPath.importCustomer,
  };

  const [searchText, setSearchText] = useState("");
  const { request } = useRequest();
  const { showConfirm } = ConfirmationBox();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [importVisible, setImportVisible] = useState(false);
  const [selected, setSelected] = useState();
  const [cricketRoles, setCricketRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: null,
    cricketRole: null,
    location: null,
    userType: "all",
  });
  const [minPoints, setMinPoints] = useState("");
  const [maxPoints, setMaxPoints] = useState("");
  const debouncedMinPoints = useDebounce(minPoints, 500);
  const debouncedMaxPoints = useDebounce(maxPoints, 500);
  const [showFilters, setShowFilters] = useState(false);

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const debouncedSearchText = useDebounce(searchText, 300);
  const navigate = useNavigate();
  const location = useLocation();
  const view = (id) => navigate(`/admin/user-manager/view/${id}`);

  const fetchData = (
    pagination,
    filters,
    searchVal = debouncedSearchText,
    minPointsVal = debouncedMinPoints,
    maxPointsVal = debouncedMaxPoints
  ) => {
    const params = new URLSearchParams();
    if (searchVal) {
      params.append("search", searchVal);
    }
    if (startDate) {
      params.append("startDate", startDate);
    }

    if (endDate) {
      params.append("endDate", endDate);
    }

    if (filters?.status) {
      params.append("status", filters.status);
    }
    if (filters?.cricketRole) {
      params.append("cricketRole", filters.cricketRole);
    }

    if (filters?.location) {
      params.append("location", filters.location);
    }

    if (filters?.userType === "coach") {
      params.append("isCoach", "true");
    } else if (filters?.userType === "academy") {
      params.append("isAcademy", "true");
    }

    if (minPointsVal !== undefined && minPointsVal !== null && minPointsVal !== "") {
      params.append("minPoints", minPointsVal);
    }
    if (maxPointsVal !== undefined && maxPointsVal !== null && maxPointsVal !== "") {
      params.append("maxPoints", maxPointsVal);
    }

    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);
    request({
      url: `${apiPath.listUsers}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setList(data.data.data);
        setPagination({
          current: pagination.current,
          total: data.data.total,
          pageSize: Number(data.data.limit),
        });
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const handleOpenStatus = (id, currentStatus) => {
    setSelected({ id, status: currentStatus });
    setStatusModalOpen(true);
  };

  const handleStatus = (id, status) => {
    request({
      url: `${apiPath.changeUserStatus}/${id}`,
      method: "PUT",
      data: { status },
      onSuccess: (data) => {
        setStatusModalOpen(false);
        fetchData(pagination, filters);
        ShowToast(
          `${data.message || "Status Changed Successfully"}`,
          Severty.SUCCESS,
        );
      },
    });
  };

  const handleDelete = (id) => {
    Modal.confirm({
      maskClosable: true,
      icon: <ExclamationCircleOutlined />,
      className: "tab_modal",
      cancelButtonProps: { color: "red" },
      content: (
        <p
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          Are you sure you want to delete this user permanently ?
        </p>
      ),
      onOk() {
        request({
          url: `${api.delete || apiPath.deleteUser}/${id}`,
          method: "DELETE",
          onSuccess: (data) => {
            fetchData(pagination, filters);
            ShowToast(
              `${data?.message || "User deleted successfully"}`,
              Severty.SUCCESS,
            );
          },
          onError: (error) => {
            const message =
              error?.response?.data?.message ||
              error?.message ||
              "Failed to delete user";
            ShowToast(message, Severty.ERROR);
          },
        });
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (_, { name, _id, avatar }) => {
        const capitalizedName = name
          ? name.charAt(0).toUpperCase() + name.slice(1)
          : "Not Available";

        return (
          <>
            {!avatar ? (
              <Avatar size={40}>{name ? name.charAt(0) : "N/A"}</Avatar>
            ) : (
              <Image className="image-index-radius" src={avatar} />
            )}
            <a className="avatar-text column-avatar-text">{capitalizedName}</a>
          </>
        );
      },
      sorter: (a, b) => {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: 200,
      render: (_, { username }) => {
        return (
          <>
            <a className="avatar-text">{username}</a>
          </>
        );
      },
    },

    {
      title: "Mobile Number",
      render: (_, { phone, countryCode }) =>
        `${countryCode ? "+" + countryCode + "-" : ""}${phone || "-"}`,
    },

    {
      title: "Points",
      dataIndex: "points",
      key: "points",
      render: (points) => (points ? points : "0"),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (location) => (location?.state ? location.state : "N/A"),
    },

    {
      title: "Status",
      render: (_, { is_delete, status, _id }) => {
        if (is_delete) {
          return <Tag color="red">Account Deleted</Tag>;
        }

        const statusConfig = {
          active: { color: "green", label: "Active" },
          banned: { color: "red", label: "Banned" },
          suspended: { color: "gold", label: "Suspended" },
          flagged: { color: "orange", label: "Flagged" },
        };

        const config = statusConfig[status] || {
          color: "default",
          label: "Unknown",
        };

        return (
          <Tag
            style={{ cursor: "pointer" }}
            color={config.color}
            onClick={() => handleOpenStatus(_id, status)}
          >
            {config.label}
          </Tag>
        );
      },
    },

    {
      title: "Registered On",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => moment(createdAt).format("DD-MM-YYYY | hh:mm:ss A"),
    },
    {
      title: "Action",

      render: (_, record) => (
        <Space>
          <Tooltip title="View User">
            <Button onClick={() => view(record._id)}>
              <i className="fa fa-eye"></i>
            </Button>
          </Tooltip>
          {!record?.is_delete && !record?.isDeleted && (
            <Tooltip
              title={
                isDeletePermissionGranted
                  ? "Delete User"
                  : "You do not have delete permission"
              }
            >
              <Button
                danger
                disabled={!isDeletePermissionGranted}
                onClick={() => handleDelete(record._id)}
              >
                <i className="fa fa-trash"></i>
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const fetchRoles = () => {
    request({
      url: `${apiPath.listRole}`,
      method: "GET",
      onSuccess: ({ data }) => {
        setLoading(false);
        setCricketRoles(data?.data.docs);
      },
      onError: (error) => {
        console.log(error);
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const fetchLocations = () => {
    request({
      url: `${apiPath.listLocations}`,
      method: "GET",
      onSuccess: ({ data }) => {
        setLoading(false);
        setLocations(data?.data);
      },
      onError: (error) => {
        console.log(error);
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const handleChangeDate = (dates) => {
    if (!dates) {
      setStartDate();
      setEndDate();
    } else {
      setStartDate(moment(dates[0]).format("YYYY-MM-DD"));
      setEndDate(moment(dates[1]).format("YYYY-MM-DD"));
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    // reset filters
    const resetFilters = {
      status: null,
      cricketRole: null,
      location: null,
      userType: "all",
    };
    setFilters(resetFilters);
    setMinPoints("");
    setMaxPoints("");

    // reset search & dates
    setSearchText("");
    setStartDate(null);
    setEndDate(null);

    // reset pagination to first page
    const resetPagination = { ...pagination, current: 1 };
    setPagination(resetPagination);

    // refetch data without filters
    fetchData(resetPagination, resetFilters, "", "", "");
  };

  // ------------ Module Permissions ------------

  const { userProfile, userPermission } = useContext(AuthContext);
  const isSuperAdmin = userProfile?.role === USERTYPE.SUPER_ADMIN;

  const isAddPermissionGranted =
    isSuperAdmin ||
    (userPermission?.USER_MANAGER?.enabled === true &&
      userPermission.USER_MANAGER.permissions.create === true);
  const isUpdatePermissionGranted =
    isSuperAdmin ||
    (userPermission?.USER_MANAGER?.enabled === true &&
      userPermission.USER_MANAGER.permissions.update === true);
  const isDeletePermissionGranted =
    isSuperAdmin ||
    (userPermission?.USER_MANAGER?.enabled === true &&
      userPermission.USER_MANAGER.permissions.delete === true);

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  useEffect(() => {
    setLoading(true);
    fetchData(pagination, filters);
  }, [
    pagination.current,
    pagination.pageSize,
    filters,
    refresh,
    debouncedSearchText,
    debouncedMinPoints,
    debouncedMaxPoints,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    fetchRoles();
    fetchLocations();
  }, []);

  useEffect(() => {
    if (location.state && location.state.username) {
      setSearchText(location.state.username);
    }
  }, [location.state]);

  return (
    <>
      <div className="tabled customerMain">
        <Row gutter={[24, 0]}>
          <Col xs={24}>
            <Card
              title="User Manager"
              extra={
                <FilterBar
                  showFilters={showFilters}
                  onToggle={() => setShowFilters((v) => !v)}
                  searchContent={
                    <Search
                      allowClear
                      placeholder="Search by Name, Email, Username"
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        setPagination((prev) => ({ ...prev, current: 1 }));
                      }}
                    />
                  }
                />
              }
            >
              {showFilters && (
                <FilterBarFilters>
                  <Select
                    placeholder="Status"
                    value={filters.status}
                    allowClear
                    onChange={(value) => handleFilterChange("status", value)}
                  >
                    <Option value={UserStatus.ACTIVE}>Active</Option>
                    <Option value={UserStatus.SUSPENDED}>Suspended</Option>
                    <Option value={UserStatus.BANNED}>Banned</Option>
                  </Select>
                  <Select
                    placeholder="Cricket Role"
                    value={filters.cricketRole}
                    allowClear
                    onChange={(value) =>
                      handleFilterChange("cricketRole", value)
                    }
                  >
                    {cricketRoles.map((role) => (
                      <Option key={role._id} value={role._id}>{role.name}</Option>
                    ))}
                  </Select>
                  <Select
                    placeholder="Location"
                    value={filters.location}
                    allowClear
                    onChange={(value) => handleFilterChange("location", value)}
                  >
                    {locations.map((location) => (
                      <Option key={location} value={location}>{location}</Option>
                    ))}
                  </Select>
                  <Select
                    placeholder="User Type"
                    value={filters.userType}
                    onChange={(value) => handleFilterChange("userType", value)}
                  >
                    <Option value="all">All</Option>
                    <Option value="coach">Coach</Option>
                    <Option value="academy">Academy</Option>
                  </Select>
                  <Input
                    type="number"
                    placeholder="Min Points"
                    value={minPoints}
                    onChange={(e) => {
                      setMinPoints(e.target.value);
                      setPagination((prev) => ({ ...prev, current: 1 }));
                    }}
                    style={{ width: 120 }}
                  />
                  <Input
                    type="number"
                    placeholder="Max Points"
                    value={maxPoints}
                    onChange={(e) => {
                      setMaxPoints(e.target.value);
                      setPagination((prev) => ({ ...prev, current: 1 }));
                    }}
                    style={{ width: 120 }}
                  />
                  <RangePicker
                    value={
                      startDate && endDate
                        ? [moment(startDate), moment(endDate)]
                        : null
                    }
                    disabledDate={(current) =>
                      current && current.isAfter(moment().endOf("day"))
                    }
                    onChange={handleChangeDate}
                  />
                  <Button icon={<UndoOutlined />} onClick={() => handleReset()}>
                    Reset
                  </Button>
                </FilterBarFilters>
              )}
              <h4 className="text-right">{ShowTotal(pagination.total || 0)}</h4>
              <Table
                loading={loading}
                columns={columns}
                dataSource={list}
                pagination={pagination}
                onChange={handleTableChange}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {visible && (
        <UserFrom
          sectionName={sectionName}
          path={api.addEdit}
          show={visible}
          hide={() => setVisible(false)}
          data={selected}
          refresh={() => setRefresh((p) => !p)}
        />
      )}

      {importVisible && (
        <ImportForm
          path={api.importFile}
          sectionName={sectionName}
          show={importVisible}
          hide={() => setImportVisible(false)}
          refresh={() => setRefresh((p) => !p)}
        />
      )}
      {/* Status Update Modal */}
      <Modal
        open={statusModalOpen}
        footer={null}
        centered
        closable
        onCancel={() => setStatusModalOpen(false)}
      >
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: 22, fontWeight: "bold", marginBottom: 20 }}>
            Update User Status
          </p>

          <Space size="middle">
            {selected?.status !== "active" && (
              <Button
                type="primary"
                style={{ background: "#52c41a", borderColor: "#52c41a" }}
                onClick={() => handleStatus(selected.id, "active")}
              >
                Active
              </Button>
            )}

            {selected?.status !== "suspended" && (
              <Button
                type="primary"
                style={{ background: "#faad14", borderColor: "#faad14" }}
                onClick={() => handleStatus(selected.id, "suspended")}
              >
                Suspended
              </Button>
            )}

            {selected?.status !== "banned" && (
              <Button
                type="primary"
                danger
                onClick={() => handleStatus(selected.id, "banned")}
              >
                Banned
              </Button>
            )}
          </Space>
        </div>
      </Modal>
    </>
  );
}

export default UserManager;
